import type { FormHandlerLaunchState } from "@/utils/formHandlerLaunch";
import type { TaskItem } from "./tasks";
import type { FormTaskAction } from "./task-actions";

/** GET /Portal/GetForm — matches swagger FacilityPatientForm */
export interface FacilityPatientForm {
  facilityFormID: number;
  facilityID: number;
  patientFormID: number;
  patientID: number;
  key: string | null;
  data: string | null;
  formFields: FormField[] | null;
  error: string | null;
}

export interface FormField {
  code: string;
  textType: string;
  data: string;
}

/** POST /Portal/UploadForm response */
export interface ApiIdResult {
  success: boolean;
  reason: string | null;
  id: number;
}

/** POST /Portal/UpdateFormComplete response */
export interface ApiBoolResult {
  success: boolean;
  reason: string | null;
}

/** FORM task `info` JSON (server / C# shape) */
export interface FacilityFormInfo {
  FacilityFormID: number;
  Action: string;
  FormName: string;
  Data: Record<string, string>;
}

/** Parse task `info` for FORM; supports PascalCase or camelCase from API. */
export function parseFacilityFormInfoFromTaskJson(infoJson: string): FacilityFormInfo | null {
  try {
    const o = JSON.parse(infoJson) as Record<string, unknown>;
    const facilityFormID = Number(o.FacilityFormID ?? o.facilityFormID);
    if (!Number.isFinite(facilityFormID)) return null;
    const FormName = String(o.FormName ?? o.formName ?? "");
    const Action = String(o.Action ?? o.action ?? "");
    const rawData = o.Data ?? o.data;
    const Data: Record<string, string> =
      rawData && typeof rawData === "object" && !Array.isArray(rawData)
        ? Object.fromEntries(
            Object.entries(rawData as Record<string, unknown>).map(([k, v]) => [
              k,
              v == null ? "" : String(v),
            ])
          )
        : {};
    return { FacilityFormID: facilityFormID, Action, FormName, Data };
  } catch {
    return null;
  }
}

/** Launch payload for `/form-handler` (not in URL). */
export function buildFormHandlerLaunchPayload(
  task: TaskItem,
  formTaskCount: number
): FormHandlerLaunchState | null {
  const info = parseFacilityFormInfoFromTaskJson(task.info);
  if (!info) return null;
  if (!Number.isFinite(info.FacilityFormID) || info.FacilityFormID <= 0) return null;
  return {
    facilityFormId: info.FacilityFormID,
    taskId: String(task.taskID),
    formTitleHint: info.FormName,
    patientLabel: info.Data.PatientName ?? "",
    formCountHint: Math.max(1, formTaskCount),
  };
}

/** Same as `buildFormHandlerLaunchPayload` but uses GET GetFormTaskAction response. */
export function buildFormHandlerLaunchPayloadFromAction(
  task: TaskItem,
  action: FormTaskAction,
  formTaskCount: number
): FormHandlerLaunchState | null {
  const id = Number(action.facilityFormID);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    facilityFormId: id,
    taskId: String(task.taskID),
    formTitleHint: action.formName ?? "",
    patientLabel: action.patientName ?? "",
    formCountHint: Math.max(1, formTaskCount),
  };
}
